<template>
  <div class="content">
    <div class="col-md-8 ml-auto mr-auto">
      <h2 class="text-center">{{ $t('emailTemplates.title') }}</h2>
      <p class="text-center">
        {{ $t('emailTemplates.description') }}
      </p>
    </div>
    <div class="row mt-5">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <edit-email-template-dialog :show="showEdit" :form-details="editEmailTemplate" @close="showEdit=false"/>
          <h4 slot="header" class="card-title">{{ $t('emailTemplates.table.title') }}</h4>
          <div>
            <div
              class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            >
              <el-select
                class="select-primary mb-3 pagination-select"
                v-model="pagination.perPage"
                placeholder="Per page"
              >
                <el-option
                  class="select-primary"
                  v-for="item in pagination.perPageOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                >
                </el-option>
              </el-select>

              <div class="row">
                <div class="col-6">
                  <base-input>
                    <el-input
                      type="search"
                      class="mb-3 search-input"
                      prefix-icon="el-icon-search"
                      :placeholder="$t('generic.searchInTablePlaceholder')"
                      v-model="searchQuery"
                      aria-controls="datatables"
                    >
                    </el-input>
                  </base-input>
                </div>
                <div class="col-6" v-if="$auth.can({email_template: 'create'}, 'permissions')">
                  <create-email-template-btn
                    :types="['password_reset', 'ref_key', 'registered', 'registration_confirm', 'temporary_password', 'top_up', 'new_mob_app_version']"
                    :languages="['lt', 'en']"
                  />
                </div>
              </div>
            </div>

            <el-table v-loading="loading" :data="queriedData" show-header :empty-text="$t('generic.noData')">
              <el-table-column type="expand">
                <template slot-scope="props">
                  <email-template-details :details="props.row"/>
                </template>
              </el-table-column>
              <el-table-column
                v-for="column in tableColumns"
                :key="column.label"
                :min-width="column.minWidth"
                :prop="column.prop"
                :label="$t(column.label)"
                :formatter="column.formatter"
              >
              </el-table-column>
              <el-table-column :min-width="30" align="right" v-if="$auth.can({email_template: 'update'}, 'permissions') || $auth.can({email_template: 'delete'}, 'permissions')">
                <div slot-scope="props">
                  <base-button
                    v-if="$auth.can({email_template: 'update'}, 'permissions')"
                    @click.native="handleEdit(props.$index, props.row)"
                    class="mr-2"
                    type="info"
                    size="sm"
                    icon
                    round
                    simple>
                    <i class="tim-icons icon-pencil"></i>
                  </base-button>
                  <base-button
                    v-if="$auth.can({email_template: 'delete'}, 'permissions')"
                    @click.native="handleDelete(props.$index, props.row)"
                    class=""
                    type="danger"
                    size="sm"
                    icon
                    round
                    simple>
                    <i class="tim-icons icon-simple-remove"></i>
                  </base-button>
                </div>
              </el-table-column>
            </el-table>
          </div>
          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div class="">
              <p class="card-category">
                {{$t('generic.tablePaginationSummary', {from: from+1, to: to, total: total})}}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>
  </div>
</template>
<script>
  import {Option, Select, Table, TableColumn, Tooltip} from 'element-ui';
  import {BaseAlert, BaseButton, BasePagination} from 'src/components';
  import Fuse from 'fuse.js';
  import {mapActions, mapState} from 'vuex'
  import CreateEmailTemplateBtn from './CreateEmailTemplateBtn'
  import swal from "sweetalert2";
  import EmailTemplateDetails from "src/components/EmailTemplate/EmailTemplateDetails";
  import EditEmailTemplateDialog from "./EditEmailTemplateDialog";

  export default {
    name: 'emailTemplates',
    components: {
      BasePagination,
      BaseAlert,
      BaseButton,
      Tooltip,
      [Select.name]: Select,
      [Option.name]: Option,
      [Table.name]: Table,
      [TableColumn.name]: TableColumn,
      CreateEmailTemplateBtn,
      EmailTemplateDetails,
      EditEmailTemplateDialog
    },
    props: {},
    computed: {
      ...mapState({
        loading: state => state.email_templates.loading,
        error: state => state.email_templates.error,
      }),
      /***
       * Returns a page from the searched data or the whole data. Search is performed in the watch section below
       */
      queriedData() {
        let result = this.tableData;
        if (this.searchedData.length > 0) {
          result = this.searchedData;
        }
        return result.slice(this.from, this.to);
      },
      to() {
        let highBound = this.from + this.pagination.perPage;
        if (this.total < highBound) {
          highBound = this.total;
        }
        return highBound;
      },
      from() {
        return this.pagination.perPage * (this.pagination.currentPage - 1);
      },
      total() {
        return this.searchedData.length > 0
          ? this.searchedData.length
          : this.tableData.length;
      }
    },
    data() {
      return {
        pagination: {
          perPage: 50,
          currentPage: 1,
          perPageOptions: [5, 10, 25, 50],
          total: 0
        },
        searchQuery: '',
        propsToSearch: ['type', 'language', 'short_name'],
        tableColumns: [
          {
            prop: 'type',
            label: 'emailTemplates.table.type',
          },
          {
            prop: 'language',
            label: 'emailTemplates.table.language',
          },
          {
            prop: 'short_name',
            label: 'emailTemplates.table.short_name',
          },
          {
            prop: 'updated_at',
            label: 'generic.updated',
            formatter: (row, column, cellValue, index) => {
              return this.$options.filters.time(cellValue)
            },
          },
        ],
        tableData: [],
        searchedData: [],
        fuseSearch: null,
        showEdit: false,
        editEmailTemplate: {},
      };
    },
    methods: {
      ...mapActions(['LoadEmailTemplates', 'DeleteEmailTemplate']),
      handleEdit(index, row) {
        this.editEmailTemplate = {
          description: row.description,
          email_content: row.email_content,
          email_content_html: row.email_content_html,
          email_subject: row.email_subject,
          id: row.id,
          language: row.language,
          short_name: row.short_name,
          type: row.type,
        };
        this.showEdit = true;
      },
      handleDelete(index, row) {
        swal({
          title: this.$t('emailTemplates.delete.text'),
          text: `'${row.short_name}' (${row.language})`,
          confirmButtonText: this.$t('emailTemplates.delete.actionBtnLabel'),
          cancelButtonText: this.$t('emailTemplates.delete.cancelBtnLabel'),
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-danger',
          cancelButtonClass: 'btn btn-secondary',
          type: 'question',
          showCancelButton: true
        }).then(result => {
          if (result.value) {
            this.DeleteEmailTemplate(row).then(() => {
              if (this.error == null) {
                // this.deleteRow(row);
                this.$notify({
                  type: 'success',
                  message: this.$t('emailTemplates.delete.deletedNotification', row),
                  icon: 'tim-icons icon-alert-circle-exc'
                })
              }

            })
          }
        });
      },
    },
    mounted() {
      this.LoadEmailTemplates();
    },
    watch: {
      /**
       * Searches through the table data by a given query.
       * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
       * @param value of the query
       */
      searchQuery(value) {
        let result = this.tableData;
        if (value !== '') {
          result = this.fuseSearch.search(this.searchQuery);
        }
        this.searchedData = result;
      },
      "$store.state.email_templates.data"(data) {
        this.tableData = data
        // Fuse search initialization.
        this.fuseSearch = new Fuse(this.tableData, {
          keys: ['type', 'language', 'short_name'],
          threshold: 0.3
        });
      }
    }
  };
</script>
<style>
  .pagination-select,
  .search-input {
    width: 200px;
  }
</style>
